import { useCallback, useState } from "react";
import type { ReferenceDeletedRestoreAvailableConflict } from "@/utils/reference/referenceCreateConflict";
import {
    ReferenceCreateConflictError,
    isReferenceDeletedRestoreAvailableConflict,
} from "@/utils/reference/referenceCreateConflict";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

type PendingRestoreForm = { description: string; isActive: boolean };

type UseReferenceRestoreOnCreateConflictOptions<TDetail extends { id: string }> = {
    deletedRestoreAvailableCode: string;
    entityLabel: string;
    formatSuccessLabel: (code: string | undefined) => string;
    restoreDeletedWithCreateValues: (payload: {
        id: string;
        description: string;
        isActive?: boolean;
    }) => Promise<TDetail>;
    onRestored: (detail: TDetail, code: string) => void;
};

export function useReferenceRestoreOnCreateConflict<TDetail extends { id: string }>({
    deletedRestoreAvailableCode,
    entityLabel,
    formatSuccessLabel,
    restoreDeletedWithCreateValues,
    onRestored,
}: UseReferenceRestoreOnCreateConflictOptions<TDetail>) {
    const [restoreConflict, setRestoreConflict] =
        useState<ReferenceDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<PendingRestoreForm | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback(
        (err: unknown): boolean => {
            if (err instanceof ReferenceCreateConflictError) {
                if (isReferenceDeletedRestoreAvailableConflict(err.conflict, deletedRestoreAvailableCode)) {
                    setRestoreConflict(err.conflict);
                    return true;
                }
            }
            return false;
        },
        [deletedRestoreAvailableCode]
    );

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || !pendingRestoreForm) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues({
                    id: restoreConflict.existingId,
                    description: pendingRestoreForm.description,
                    isActive: pendingRestoreForm.isActive,
                });
                const code = restoreConflict.existingCode;
                clearRestoreConflict();
                showToastSuccess(`${formatSuccessLabel(code)} restored successfully!`);
                onRestored(restored, code);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [
            restoreConflict,
            pendingRestoreForm,
            restoreDeletedWithCreateValues,
            formatSuccessLabel,
            onRestored,
            clearRestoreConflict,
        ]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: `Restore removed ${entityLabel}?`,
        body: restoreConflict
            ? `A removed ${entityLabel} already exists with code "${restoreConflict.existingCode}". Restore it with the description and Active choice you entered?`
            : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        restoreConflict,
        pendingRestoreForm,
        restoringDeleted,
        handleCreateError,
        setPendingRestoreForm,
        clearRestoreConflict,
        confirmRestore,
        restoreModalProps,
    };
}
